// Pure period-over-period growth math for the inline growth row rendered beneath
// each toggled metric in custom KPI reports. Kept dependency-free (no React, no
// dayjs/charts chain) so it can be unit-tested in isolation and imported cheaply.

export interface WowGrowthColumn {
  key: string
}

export interface WowGrowthRowData {
  [key: string]: string | number | undefined
}

export interface WowGrowthCell {
  delta: number
  // Percent change vs. the prior period; null when the prior period is 0 (the
  // percent change is undefined) or there is no prior period.
  pct: number | null
}

// Per-column growth for a single metric row: the absolute delta against the
// immediately-preceding column plus the percent change. The first data column
// has no prior period, so it maps to null.
export function computeWowGrowthValues(
  row: WowGrowthRowData,
  columns: WowGrowthColumn[],
): Record<string, WowGrowthCell | null> {
  const result: Record<string, WowGrowthCell | null> = {}
  columns.forEach((column, index) => {
    if (column.key === "metric") return
    const previousKey = columns[index - 1]?.key
    // Column 0 is the metric label; column 1 is the first data period, which has
    // no prior period to compare against.
    if (index <= 1 || !previousKey || previousKey === "metric") {
      result[column.key] = null
      return
    }
    const current = parseFloat(String(row[column.key])) || 0
    const previous = parseFloat(String(row[previousKey])) || 0
    const delta = current - previous
    result[column.key] = {
      delta,
      pct: previous !== 0 ? (delta / previous) * 100 : null,
    }
  })
  return result
}
